import { useEffect, useRef, useState } from 'react'
import type { PayloadMeta, EditionNavigation } from '../types/payload'
import { isEmbeddedExport } from '../utils/embeddedPayload'
import styles from './Header.module.css'

interface HeaderProps {
  meta?: PayloadMeta
  navigation?: EditionNavigation
  onEditionChange?: (period: string) => void
  onExport?: () => void
  isExporting?: boolean
  onCopyLink?: () => void | Promise<void>
}

export function Header({ meta, navigation, onEditionChange, onExport, isExporting, onCopyLink }: HeaderProps) {
  const embedded = isEmbeddedExport()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  const handleDownloadClick = () => {
    setIsMenuOpen(false)
    onExport?.()
  }

  const handleCopyLinkClick = async () => {
    await onCopyLink?.()
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  return (
    <header className={styles.header}>
      <div className={`container ${styles.inner}`}>
        <nav className={styles.nav}>
          {meta && (
            <span className={styles.edition}>
              {meta.edition_label}
            </span>
          )}

          {navigation && navigation.editions.length > 1 && (
            <div className={styles.editionPicker}>
              <span className="microlabel">Edição</span>
              <select
                className={styles.select}
                value={navigation.current}
                onChange={e => onEditionChange?.(e.target.value)}
                aria-label="Selecionar edição"
              >
                {[...navigation.editions].reverse().map(ed => (
                  <option key={ed} value={ed}>{ed}</option>
                ))}
              </select>
            </div>
          )}

          {onExport && !embedded && (
            <div className={styles.exportMenu} ref={menuRef}>
              <button
                type="button"
                className={styles.exportButton}
                onClick={() => setIsMenuOpen(open => !open)}
                disabled={isExporting}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isExporting ? 'Gerando…' : 'Exportar'}
              </button>

              {isMenuOpen && (
                <div className={styles.exportMenuPanel} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={handleDownloadClick}
                  >
                    Baixar HTML
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={handleCopyLinkClick}
                  >
                    {linkCopied ? 'Link copiado!' : 'Copiar Link da Edição'}
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
    </header>
  )
}
